import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { SampleReport } from "@/components/SampleReport";
import logo from "@/assets/logo.png";
import {
  Moon, Flame, Battery, Brain, Heart, Check, Lock,
  BookOpen, Target, ClipboardCheck, PenLine, ChevronDown, ShieldCheck, FileText,
  Stethoscope, TrendingUp, MessageSquareText, ListChecks, CalendarCheck, HeartPulse, Wind,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1611077094733-db54f4445a4e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const TEA_IMG =
  "https://images.unsplash.com/photo-1474859569645-e0def92b02bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const PAINS = [
  { icon: Moon, t: "Nights that don't rest you", d: "Lying awake, waking at 3am, dragging through the next day." },
  { icon: Flame, t: "Flushes at the worst moments", d: "Sudden heat and night sweats you can't seem to predict." },
  { icon: Battery, t: "An energy tank on empty", d: "Tired in a way that sleep alone doesn't seem to fix." },
  { icon: Brain, t: "Words on the tip of your tongue", d: "Brain fog that makes you doubt yourself at work and home." },
];

const LOOP = [
  { icon: BookOpen, t: "Learn", d: "A short, clear lesson, never a wall of text." },
  { icon: Target, t: "Today's Action", d: "One simple, doable thing for today." },
  { icon: ClipboardCheck, t: "Do It", d: "A gentle checklist to make it real." },
  { icon: PenLine, t: "Reflect", d: "One question to check in with yourself." },
];

const WEEKS = [
  { n: 1, t: "Understand", d: "Make sense of your changing body, with no blame and no jargon.", days: "Days 1 to 7" },
  { n: 2, t: "Support", d: "Food, protein, fibre, movement and strength that fit real life.", days: "Days 8 to 14" },
  { n: 3, t: "Feel Better", d: "Practical tools for sleep, flushes, stress and focus.", days: "Days 15 to 21" },
  { n: 4, t: "Make It Yours", d: "Build your own lasting reset plan to carry forward.", days: "Days 22 to 30" },
];

const INCLUDED = [
  "30 guided daily lessons, bite sized and doable",
  "Daily action and reflection to build real habits",
  "Simple check ins for sleep, energy and mood",
  "A self management toolkit tailored to you",
  "Progress trends you can actually see",
  "A Doctor's Appointment Report to take to your GP",
  "A personal support plan, both produced once you finish all 30 days",
  "Full access all year, cancel whenever you like",
];

const REPORT = [
  { icon: TrendingUp, t: "Your symptom patterns", d: "Sleep, hot flushes, mood and energy, tracked over 30 days so the picture is clear." },
  { icon: ClipboardCheck, t: "What you've tried", d: "The changes you tested and how they actually made you feel." },
  { icon: MessageSquareText, t: "Questions to ask", d: "A focused list of the right questions for your GP or specialist." },
  { icon: ListChecks, t: "Your priorities", d: "What matters most to you, in your own words, ready to talk through." },
];

const GET = [
  { icon: CalendarCheck, tint: "sage", t: "One clear step a day", d: "No overwhelm. Open the app and see exactly what to do today, in 5 to 15 minutes." },
  { icon: HeartPulse, tint: "brand", t: "Track how you feel", d: "Log sleep, hot flushes, mood and energy with a couple of taps, whenever they hit." },
  { icon: TrendingUp, tint: "amber", t: "See your patterns", d: "Watch your own trends build over 30 days so what's really going on becomes clear." },
  { icon: Wind, tint: "brand", t: "Calming tools & routines", d: "Guided breathing, sleep and stress supports you can reach for the moment you need them." },
  { icon: FileText, tint: "sage", t: "A report for your doctor", d: "Everything comes together into a printable summary you can take to your appointment." },
  { icon: ClipboardCheck, tint: "amber", t: "Weekly summaries", d: "A gentle look back at the end of each week so you can see how far you've come." },
];

const FAQS = [
  { q: "Is this medical advice?", a: "No. This is an educational, self management program to help you understand and support your wellbeing. It does not diagnose, treat or replace personal medical care. Always speak to a healthcare professional about your symptoms." },
  { q: "Do I have to do it on set dates?", a: "Never. Complete a day whenever you're ready, and finishing one day simply unlocks the next. There's no shame in missing a day." },
  { q: "How much time does it take each day?", a: "Most days take 5 to 15 minutes. You tell us how much time you have during onboarding, and we keep it realistic." },
  { q: "When do I get my doctor's report and plan?", a: "Everything you enter each day is saved along the way, and you can see weekly summaries as you go. Your Doctor's Appointment Report and personal support plan are both produced once you complete all 30 days, ready to print or show on your phone." },
  { q: "What if I'm not sure which stage I'm in?", a: "That's completely fine. 'I'm not sure' is one of the options, and the program meets you wherever you are." },
  { q: "How does billing work?", a: "Choose the plan that suits you: £47.99 a year (best value) or £4.99 a month. Either way you get full access to everything, it renews automatically, and you can cancel anytime from your profile, no hidden fees." },
];

export default function Landing() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(0);
  const go = () => navigate("/start");

  return (
    <div className="bg-app min-h-screen" data-testid="landing-page">
      {/* Header */}
      <header
        className="sticky top-0 z-40 backdrop-blur-md border-b border-subtle"
        style={{ backgroundColor: "rgba(250,248,245,0.85)" }}
      >
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <img src={logo} alt="Meno Flames and Finesse" className="h-12 w-auto" data-testid="header-logo" />
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              data-testid="header-login-btn"
              onClick={() => navigate("/login")}
              className="text-sm font-semibold text-ink-soft hover:text-ink transition-colors px-2"
            >
              Log in
            </button>
            <button
              data-testid="header-cta-btn"
              onClick={go}
              className="bg-brand hover:bg-[var(--brand-hover)] text-white text-sm font-semibold px-4 sm:px-5 py-2.5 rounded-full transition-colors"
            >
              Start free trial
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-12 pb-14 sm:pt-20 grid lg:grid-cols-2 gap-10 items-center">
        <div className="animate-fade-rise">
          <div className="inline-flex items-center gap-2 bg-sage text-brand rounded-full px-4 py-1.5 text-xs font-semibold tracking-wide mb-6">
            <Stethoscope size={14} /> Walk into your next appointment prepared
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-ink leading-[1.05] tracking-tight">
            The 30 Day<br />Menopause Reset
          </h1>
          <p className="text-base sm:text-lg text-ink-soft leading-relaxed mt-6 max-w-lg">
            Finally, a simple way to understand your menopause, not just read about it.
          </p>
          <p className="text-base text-ink-soft leading-relaxed mt-4 max-w-lg">
            Each day, the app guides you through one manageable step to help you track
            how you're feeling, understand your patterns, build supportive routines and
            create a personal menopause report to take to your doctor.
          </p>
          <p className="text-base text-ink-soft leading-relaxed mt-4 max-w-lg">
            In 30 days, you'll have more than information. You'll have a clearer picture
            of your experience.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <button
              data-testid="hero-cta-btn"
              onClick={go}
              className="bg-brand hover:bg-[var(--brand-hover)] text-white font-semibold px-8 py-4 rounded-full text-base transition-all hover:shadow-lg w-full sm:w-auto"
            >
              Start my 7-day free trial
            </button>
            <span className="text-sm text-ink-muted">7 days free · No card needed · then £47.99/yr or £4.99/mo</span>
          </div>
          <div className="flex items-center gap-2 mt-6 text-sm text-ink-muted">
            <ShieldCheck size={16} className="text-brand" /> No card needed to start your free trial
          </div>
        </div>
        <div className="relative animate-soft-scale max-w-[340px] sm:max-w-[380px] mx-auto lg:mr-0 lg:ml-auto w-full">
          <div className="rounded-[2rem] overflow-hidden card-shadow-hover aspect-[4/5]">
            <img src={HERO_IMG} alt="A woman feeling calm and in control" className="w-full h-full object-cover" />
          </div>
          <div className="absolute -bottom-5 -left-2 sm:left-4 bg-white rounded-2xl card-shadow px-5 py-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-sage flex items-center justify-center">
              <Heart size={18} className="text-brand" />
            </div>
            <div>
              <div className="text-xs text-ink-muted">Day 7 of 30</div>
              <div className="text-sm font-semibold text-ink">You're building momentum</div>
            </div>
          </div>
        </div>
      </section>

      {/* What you get in the app */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24" data-testid="what-you-get">
        <div className="text-center max-w-2xl mx-auto">
          <p className="overline-label">What's inside</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-ink mt-3">
            Exactly what you get in the app
          </h2>
          <p className="text-base text-ink-soft leading-relaxed mt-4">
            Not another course to get through, real, practical support you'll actually use each day.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {GET.map((g, i) => {
            const Icon = g.icon;
            const tile = g.tint === "brand"
              ? { bg: "var(--brand)", color: "#fff" }
              : g.tint === "amber"
                ? { bg: "var(--amber)", color: "var(--brand)" }
                : { bg: "var(--sage)", color: "var(--brand)" };
            return (
              <div
                key={i}
                data-testid={`get-item-${i}`}
                className="bg-white rounded-3xl p-7 card-shadow hover:card-shadow-hover transition-all"
              >
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: tile.bg }}>
                  <Icon size={24} style={{ color: tile.color }} />
                </div>
                <h3 className="font-serif text-xl text-ink mt-5">{g.t}</h3>
                <p className="text-sm text-ink-soft leading-relaxed mt-2">{g.d}</p>
              </div>
            );
          })}
        </div>
        <div className="text-center mt-12">
          <button
            data-testid="get-cta-btn"
            onClick={go}
            className="bg-brand hover:bg-[var(--brand-hover)] text-white font-semibold px-8 py-4 rounded-full text-base transition-all hover:shadow-lg w-full sm:w-auto"
          >
            Start my 7-day free trial
          </button>
        </div>
      </section>

      {/* Pain points — the reason you need this, right at the top */}
      <section className="bg-app-secondary py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <p className="overline-label">If this sounds familiar</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-ink mt-3 max-w-2xl">
            You're not imagining it, and you're not alone
          </h2>
          <p className="text-base text-ink-soft leading-relaxed mt-4 max-w-2xl">
            If any of this feels like your everyday, this is exactly why the reset exists,
            to help you make sense of what's happening and feel like yourself again.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {PAINS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="bg-white rounded-2xl p-6 card-shadow">
                  <div className="w-11 h-11 rounded-xl bg-sage flex items-center justify-center mb-4">
                    <Icon size={20} className="text-brand" />
                  </div>
                  <h3 className="font-semibold text-ink text-base mb-1.5">{p.t}</h3>
                  <p className="text-sm text-ink-soft leading-relaxed">{p.d}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-10">
            <button
              data-testid="pains-cta-btn"
              onClick={go}
              className="bg-brand hover:bg-[var(--brand-hover)] text-white font-semibold px-8 py-4 rounded-full text-base transition-all hover:shadow-lg w-full sm:w-auto"
            >
              Start my 7-day free trial
            </button>
          </div>
        </div>
      </section>

      {/* MAIN USP — Doctor's Appointment Report */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24" data-testid="doctor-report-section">
        <div className="bg-brand rounded-[2rem] p-8 sm:p-14 text-white grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: "var(--amber)" }}>
              <Stethoscope size={15} /> Your doctor's appointment report
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold mt-4 leading-tight">
              Never freeze up in a 10 minute appointment again
            </h2>
            <p className="text-base text-white/75 leading-relaxed mt-5">
              Menopause appointments are short, and it's so easy to forget half of
              what you meant to say the moment you sit down. As you move through the
              reset, the app quietly builds your personal Doctor's Appointment Report:
              a clear, printable summary of your symptoms, what you've tried, and
              exactly what to ask, so you walk in prepared and get far more from your
              time with your GP.
            </p>
            <p className="text-sm text-white/55 leading-relaxed mt-4">
              It doesn't replace medical care. It helps you get more from it.
            </p>
            <button
              data-testid="report-cta-btn"
              onClick={go}
              className="mt-7 bg-white text-brand hover:bg-[var(--sage)] font-semibold px-7 py-3.5 rounded-full transition-colors w-full sm:w-auto"
            >
              Start my 7-day free trial
            </button>
          </div>

          {/* report preview card */}
          <div className="bg-white rounded-3xl p-6 sm:p-7 text-ink card-shadow">
            <div className="flex items-center gap-3 pb-4 border-b border-subtle">
              <div className="w-10 h-10 rounded-xl bg-sage flex items-center justify-center">
                <FileText size={18} className="text-brand" />
              </div>
              <div>
                <div className="font-serif text-lg text-ink leading-tight">Your Appointment Report</div>
                <div className="text-ink-muted text-xs">Ready to print or show on your phone</div>
              </div>
            </div>
            <div className="mt-4 space-y-3">
              {REPORT.map((r, i) => {
                const Icon = r.icon;
                return (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-app flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon size={16} className="text-brand" />
                    </div>
                    <div>
                      <div className="font-semibold text-ink text-sm">{r.t}</div>
                      <div className="text-ink-soft text-xs leading-relaxed mt-0.5">{r.d}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-5 flex items-center gap-2 bg-sage rounded-xl px-4 py-3">
              <Lock size={14} className="text-brand flex-shrink-0" />
              <span className="text-brand text-xs font-medium">Produced once you complete all 30 days</span>
            </div>
          </div>
        </div>
      </section>

      {/* Sample report */}
      <section className="bg-app-secondary py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="text-center">
            <p className="overline-label">See a real example</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-ink mt-3">
              Exactly what you'll walk away with
            </h2>
            <p className="text-base text-ink-soft leading-relaxed mt-4 max-w-xl mx-auto">
              Here's a sample of the report the app builds for you. Yours is filled in
              from your own daily notes, ready to hand over at your appointment.
            </p>
          </div>
          <div className="mt-10">
            <SampleReport />
          </div>
          <div className="text-center mt-10">
            <button
              data-testid="sample-cta-btn"
              onClick={go}
              className="bg-brand hover:bg-[var(--brand-hover)] text-white font-semibold px-8 py-4 rounded-full text-base transition-all hover:shadow-lg w-full sm:w-auto"
            >
              Start my 7-day free trial
            </button>
          </div>
        </div>
      </section>

      {/* What it is */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <div className="rounded-[2rem] overflow-hidden card-shadow aspect-[4/3] order-2 lg:order-1">
          <img src={TEA_IMG} alt="A quiet, warm morning moment" className="w-full h-full object-cover" />
        </div>
        <div className="order-1 lg:order-2">
          <p className="overline-label">What the reset is</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-ink mt-3">
            A journey, not another PDF to ignore
          </h2>
          <p className="text-base text-ink-soft leading-relaxed mt-5">
            This isn't a library of content to wade through. It's a warm, guided
            path you walk one day at a time. Each day gives you something small to
            understand, one action to take, and a moment to reflect.
          </p>
          <p className="text-base text-ink-soft leading-relaxed mt-4">
            Open the app, see today's action, complete it, check in, and watch your
            progress grow. That's the whole rhythm, gentle and repeatable.
          </p>
        </div>
      </section>

      {/* How it works — the daily loop */}
      <section className="bg-brand py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: "var(--amber)" }}>
            How each day works
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mt-3 max-w-2xl">
            The same calming rhythm, every single day
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {LOOP.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="rounded-2xl p-6" style={{ backgroundColor: "rgba(255,255,255,0.08)" }}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: "var(--amber)" }}>
                      <Icon size={18} className="text-brand" />
                    </div>
                    <span className="text-white/50 text-sm font-semibold">0{i + 1}</span>
                  </div>
                  <h3 className="font-serif text-xl text-white mb-1.5">{s.t}</h3>
                  <p className="text-sm text-white/70 leading-relaxed">{s.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <p className="overline-label">What's included</p>
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-ink mt-3 max-w-2xl">
          Everything you need, nothing you don't
        </h2>
        <div className="grid sm:grid-cols-2 gap-4 mt-10">
          {INCLUDED.map((item, i) => (
            <div key={i} className="flex items-start gap-3 bg-white rounded-2xl p-5 card-shadow">
              <div className="w-6 h-6 rounded-full bg-sage flex items-center justify-center flex-shrink-0 mt-0.5">
                <Check size={14} className="text-brand" strokeWidth={3} />
              </div>
              <span className="text-ink-soft text-base">{item}</span>
            </div>
          ))}
        </div>
      </section>

      {/* 30 day journey */}
      <section className="bg-app-secondary py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <p className="overline-label">The 30 day journey</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-ink mt-3">
            Four gentle weeks
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {WEEKS.map((w) => (
              <div key={w.n} className="bg-white rounded-2xl p-6 card-shadow">
                <div className="text-xs font-semibold tracking-wide text-accent-c mb-2">
                  WEEK {w.n} · {w.days}
                </div>
                <h3 className="font-serif text-xl text-ink mb-2">{w.t}</h3>
                <p className="text-sm text-ink-soft leading-relaxed">{w.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Your report — recap */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="bg-sage rounded-[2rem] p-8 sm:p-14">
          <div className="flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-brand">
            <Stethoscope size={15} /> More than a plan
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-ink mt-3 max-w-2xl">
            Everything you note becomes something useful
          </h2>
          <p className="text-base text-ink-soft leading-relaxed mt-4 max-w-2xl">
            Nothing you enter is wasted. Each day is saved, you can look back at a
            summary at the end of every week, and once you finish all 30 days it comes
            together into two things you keep for good: your personal Menopause Support
            Plan and the Doctor's Appointment Report you can take straight to your GP.
          </p>
        </div>
      </section>

      {/* Toolkit */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="bg-sage rounded-[2rem] p-8 sm:p-14">
          <p className="overline-label">Your toolkit</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-ink mt-3 max-w-2xl">
            Calm tools, ready when you need them
          </h2>
          <p className="text-base text-ink-soft leading-relaxed mt-4 max-w-2xl">
            Breathwork timers, a balanced plate template, a hot flash ready kit,
            evening wind downs and more, recommended based on what's bothering you
            most.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            {["Breathwork & Calm", "Nutrition Guides", "Sleep Hygiene", "Movement", "Symptom Relief", "Focus & Clarity"].map((t) => (
              <span key={t} className="bg-white text-ink-soft text-sm font-medium px-4 py-2 rounded-full">
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <p className="overline-label text-center block">Questions</p>
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-ink mt-3 text-center">
          Frequently asked
        </h2>
        <div className="mt-10 space-y-3">
          {FAQS.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl card-shadow overflow-hidden">
              <button
                data-testid={`faq-${i}`}
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
              >
                <span className="font-semibold text-ink text-base">{f.q}</span>
                <ChevronDown
                  size={20}
                  className={`text-ink-muted flex-shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`}
                />
              </button>
              {openFaq === i && (
                <div className="px-6 pb-5 text-ink-soft text-sm leading-relaxed">{f.a}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Medical disclaimer */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-16">
        <div className="border border-subtle rounded-2xl p-6 bg-app-secondary">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck size={16} className="text-brand" />
            <span className="text-sm font-semibold text-ink">Medical disclaimer</span>
          </div>
          <p className="text-sm text-ink-muted leading-relaxed">
            The 30 Day Menopause Reset is an educational and self management program.
            It is not medical advice and does not diagnose, treat or cure any
            condition. It does not replace care from a qualified healthcare
            professional. Always consult your doctor about your symptoms, especially
            before making changes to medication, and seek urgent care for any serious
            or sudden symptoms.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-brand py-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
            Give yourself the next 30 days
          </h2>
          <p className="text-white/75 text-base sm:text-lg mt-5 max-w-xl mx-auto">
            Start understanding your body, supporting your wellbeing, and feeling
            more in control, one gentle day at a time.
          </p>
          <button
            data-testid="final-cta-btn"
            onClick={go}
            className="mt-8 bg-white text-brand hover:bg-[var(--sage)] font-semibold px-8 py-4 rounded-full text-base transition-colors"
          >
            Start my 7-day free trial
          </button>
          <p className="text-white/50 text-sm mt-5">7 days free · No card needed · then £47.99/yr or £4.99/mo</p>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-5 sm:px-8 py-10 text-center">
        <img src={logo} alt="Meno Flames and Finesse" className="h-20 w-auto mx-auto" />
        <p className="text-sm text-ink-muted mt-3">© {new Date().getFullYear()} · The 30 Day Menopause Reset · An educational wellbeing program</p>
        <p data-testid="footer-company" className="text-xs text-ink-muted mt-2">
          The Faybelle Collection t/a Meno Flames and Finesse · <a href="mailto:hello@menoflamesandfinesse.com" data-testid="footer-email" className="underline hover:text-brand">hello@menoflamesandfinesse.com</a>
        </p>
      </footer>
    </div>
  );
}
